import { Flex } from "@chakra-ui/react";
import { useAnimate, useInView } from "framer-motion";
import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { AnalyzeFunnelsChart } from "./chart";

function useAnalyzeFunnelsAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // "Login"
                ["#login_green_bar", { opacity: 1, y: 0 }, { duration: 1, ease: "easeOut" }],
                ["#login_purple_bar", { opacity: 1, y: 0 }, { at: "<", duration: 1, ease: "easeOut" }],
                ["#login_green_bar_text", { opacity: 1 }, { duration: 0.2 }],
                ["#login_purple_bar_text", { opacity: 1 }, { at: "<", duration: 0.2 }],

                // "Add to cart"
                ["#cart_chart_label", { opacity: 1 }, { at: "-1" }],
                ["#cart_green_100_bar", { opacity: 0.2, x: 0 }, { duration: 0.75, ease: "easeInOut" }],
                ["#cart_purple_100_bar", { opacity: 0.2, x: 0 }, { at: "<", duration: 0.75, ease: "easeInOut" }],
                ["#cart_green_38_bar", { opacity: 1, y: 0 }, { at: "-0.5", duration: 1, ease: "easeOut" }],
                ["#cart_purple_27_bar", { opacity: 1, y: 0 }, { at: "<", duration: 1, ease: "easeOut" }],
                ["#cart_green_bar_text", { opacity: 1 }, { duration: 0.2 }],
                ["#cart_purple_bar_text", { opacity: 1 }, { at: "<", duration: 0.2 }],

                // "Order completed"
                ["#completed_chart_label", { opacity: 1 }, { at: "-1" }],
                ["#completed_green_38_bar", { opacity: 0.2, x: 0 }, { duration: 0.75, ease: "easeInOut" }],
                ["#completed_purple_27_bar", { opacity: 0.2, x: 0 }, { at: "<", duration: 0.75, ease: "easeInOut" }],
                ["#completed_green_14_bar", { opacity: 1, y: 0 }, { at: "-0.5", duration: 1, ease: "easeOut" }],
                ["#completed_purple_8_bar", { opacity: 1, y: 0 }, { at: "<", duration: 1, ease: "easeOut" }],
                ["#completed_green_bar_text", { opacity: 1 }, { duration: 0.2 }],
                ["#completed_purple_bar_text", { opacity: 1 }, { at: "<", duration: 0.2 }],
              ]
            : [
                // Reset
                [".analyze-funnel-fade-element", { opacity: 0 }, { duration: 0.01 }],
                ["#login_green_bar", { y: 150, opacity: 0.4 }, { at: "<", duration: 0.01 }],
                ["#login_purple_bar", { y: 150, opacity: 0.4 }, { at: "<", duration: 0.01 }],
                ["#cart_green_38_bar", { y: 100 }, { at: "<", duration: 0.01 }],
                ["#cart_purple_27_bar", { y: 80 }, { at: "<", duration: 0.01 }],
                ["#completed_green_14_bar", { y: 35 }, { at: "<", duration: 0.01 }],
                ["#completed_purple_8_bar", { y: 25 }, { at: "<", duration: 0.01 }],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function AnalyzeFunnelsAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useAnalyzeFunnelsAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.8 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      boxSize="100%"
      pos="relative"
      bgGradient="linear(140deg, green.50 -40%, mint 160%)"
      p={{ base: 6, lg: 8, xl: 10 }}
    >
      <Flex pos="relative" flex={1} mx="auto">
        <AnalyzeFunnelsChart />
      </Flex>
    </Flex>
  );
}
